import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, Users, GraduationCap, Stethoscope, HardHat, MapPin, FileText, Briefcase, Hash, Calendar, FileSpreadsheet, FileArchive, Loader2, Wand2, Archive, RefreshCw, Merge, Trash2, Plus } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useFetch, Spinner } from '../lib/useFetch';
import { companiesApi, workersApi, formazioneApi, visiteApi, dpiApi, reportsApi, rolesApi } from '../lib/api';
import { statusBadge } from './FormazionePage';
import { fmtDate } from '../lib/format';
import { toast } from 'sonner';
import AttachmentsPanel from '../components/AttachmentsPanel';
import AutoProfileDialog from '../components/AutoProfileDialog';
import EntityFormModal from '../components/EntityFormModal';
import { companyFields, workerFields, formazioneFields, visitaFields } from '../lib/entitySchemas';
import { DismissCompanyDialog, ReactivateCompanyDialog, RehireWorkerDialog, DismissWorkerDialog } from '../components/DismissalDialogs';
import { FolderArchive, Pencil, Mail, IdCard, ArrowRightLeft, Paperclip, UserPlus, UserMinus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCrudActions } from '../lib/useCrudActions';
import { labelFor as categoryLabel } from '../lib/formazioneCategories';
import ReassignCompanyDialog from '../components/ReassignCompanyDialog';
import ContrattoServiziPanel from '../components/ContrattoServiziPanel';
import VisitaAttachmentsDialog from '../components/VisitaAttachmentsDialog';
import { resolveSortKey } from '../lib/italianNames';
import SortAZButton from '../components/SortAZButton';

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [showDismessi, setShowDismessi] = useState(false);

  const { data: company, loading: lc, reload: reloadCompany } = useFetch(() => companiesApi.get(id), [id]);
  const { data: workers, loading: lw, reload: reloadWorkers } = useFetch(
    () => workersApi.list({ companyId: id, status: showDismessi ? 'dismesso' : undefined }),
    [id, showDismessi],
  );
  const { data: formazione, reload: reloadFormazione } = useFetch(() => formazioneApi.list({ companyId: id }), [id]);
  const { data: sanitario, reload: reloadSanitario } = useFetch(() => visiteApi.sanitario({ companyId: id }), [id]);
  const { data: visiteRaw, reload: reloadVisiteRaw } = useFetch(() => visiteApi.list({ companyId: id }), [id]);
  const { data: dpi } = useFetch(() => dpiApi.list({ companyId: id }), [id]);
  const workerCrud = useCrudActions({ api: workersApi, label: 'lavoratore', onChange: () => { reloadWorkers(); reloadSanitario(); } });
  const formazioneCrud = useCrudActions({ api: formazioneApi, label: 'corso', onChange: reloadFormazione });
  const visitaCrud = useCrudActions({
    api: visiteApi,
    label: 'visita',
    onChange: () => { reloadSanitario(); reloadVisiteRaw(); },
  });

  const doDownload = async (type) => {
    setDownloading(type);
    try {
      if (type === 'excel') await reportsApi.companyExcel(id);
      else await reportsApi.companyZip(id);
      toast.success('Download avviato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore download'); }
    finally { setDownloading(null); }
  };

  const workerName = (wid) => {
    const w = (workers || []).find((x) => x.id === wid);
    return w ? `${w.firstName} ${w.lastName}` : '—';
  };

  const counts = useMemo(() => ({
    workers: workers?.length || 0,
    formazione: formazione?.length || 0,
    visite: sanitario?.totale || 0,
    dpi: dpi?.length || 0,
  }), [workers, formazione, sanitario, dpi]);

  if (lc) return <Spinner />;
  if (!company) {
    return <div className="text-center text-slate-500 py-12">Azienda non trovata.</div>;
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-slate-700">
          <ArrowLeft size={15} /> Indietro
        </button>
        <Link to="/aziende" className="text-sm text-slate-500 hover:text-[#023F80]">Elenco aziende</Link>
        <span className="text-slate-300">/</span>
        <span className="text-sm font-semibold text-slate-800 truncate">{company.name}</span>
        <div className="ml-auto flex gap-2 flex-wrap" data-testid="company-export-actions">
          {user?.role !== 'client' && company?.status !== 'dismesso' && (
            <button
              onClick={() => setAutoOpen(true)}
              data-testid="company-auto-profile-btn"
              title="Profilazione automatica dei campi vuoti"
              className="ro-hide flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700"
            >
              <Wand2 size={14} className="text-[#023F80]" /> Profila ora
            </button>
          )}
          {user?.role !== 'client' && company?.status === 'dismesso' && (
            <button
              onClick={() => setReactivateOpen(true)}
              data-testid="company-reactivate-btn"
              className="ro-hide flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
            >
              <RefreshCw size={14} /> Riattiva azienda
            </button>
          )}
          {user?.role !== 'client' && company?.status !== 'dismesso' && (
            <button
              onClick={() => setDismissOpen(true)}
              data-testid="company-dismiss-btn"
              className="ro-hide flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-red-200 hover:bg-red-50 text-xs font-semibold text-red-700"
            >
              <Archive size={14} /> Dismetti
            </button>
          )}
          <button
            onClick={() => doDownload('excel')}
            disabled={downloading}
            data-testid="company-export-excel"
            title="Scarica Excel scadenze"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700 disabled:opacity-50"
          >
            {downloading === 'excel' ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} className="text-green-700" />}
            Excel
          </button>
          <button
            onClick={() => doDownload('zip')}
            disabled={downloading}
            data-testid="company-export-zip"
            title="Pacchetto ispezione (Excel + Allegati)"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#023F80] hover:bg-[#1e293b] text-xs font-bold text-white disabled:opacity-50"
          >
            {downloading === 'zip' ? <Loader2 size={14} className="animate-spin" /> : <FileArchive size={14} className="text-[#FFCB05]" />}
            Pacchetto ispezione
          </button>
        </div>
      </div>

      {/* Card intestazione */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#023F80] to-[#023F80] text-white p-6 sm:p-8">
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-[#FFCB05]/15" />
        <div className="absolute right-10 bottom-0 w-20 h-20 rounded-full bg-white/5" />
        <div className="relative flex items-start gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <Building2 size={30} className="text-[#FFCB05]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight">{company.name}</h1>
              {company.status === 'dismesso' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-400/20 text-red-200 border border-red-300/30">
                  <Archive size={12} /> Dismessa il {fmtDate(company.dismessoAt) || '—'}
                </span>
              )}
            </div>
            {company.status === 'dismesso' && company.dismissalReason && (
              <p className="text-xs text-white/70 mt-2 italic">Motivo dismissione: {company.dismissalReason}</p>
            )}
            <p className="text-white/70 mt-2 text-sm leading-relaxed">{company.address || '—'}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {company.sede && (
                <Tag icon={MapPin}>{company.sede}</Tag>
              )}
              {company.contract && company.contract !== '-' && (
                <Tag icon={Briefcase}>Contratto {company.contract}</Tag>
              )}
              {company.disk && company.disk !== '-' && (
                <Tag icon={Hash}>Spazio {company.disk}</Tag>
              )}
              {company.ore && company.ore !== '-' && (
                <Tag icon={Calendar}>Ore: {company.ore.replace(/\n/g, ' ')}</Tag>
              )}
              {company.email && (
                <Tag icon={Briefcase}>{company.email}</Tag>
              )}
              {company.macrosettore && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFCB05] text-[#023F80] text-[10px] font-bold uppercase tracking-wider">
                  {company.macrosettore}
                </span>
              )}
              {company.over15Dipendenti && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-500/30 border border-orange-300 text-orange-100 text-[10px] font-bold uppercase tracking-wider">
                  &gt;15 dipendenti
                </span>
              )}
              {company.controlloVVF && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/30 border border-red-300 text-red-100 text-[10px] font-bold uppercase tracking-wider">
                  Controllo VVF
                </span>
              )}
              {Array.isArray(company.profili) && company.profili.filter((p) => !p.startsWith('rischio_')).map((p) => (
                <span key={p} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/10 border border-white/20 text-white/90 text-[10px] font-semibold">
                  {p.replace(/_/g, ' ')}
                </span>
              ))}
              {company.rischio && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${rischioHeaderClass(company.rischio)}`} data-testid="company-header-rischio">
                  Rischio {company.rischio}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Users} label="Lavoratori" value={counts.workers} bg="#e0e7ff" color="#023F80" />
        <Kpi icon={GraduationCap} label="Corsi formazione" value={counts.formazione} bg="#dcfce7" color="#15803d" />
        <Kpi icon={Stethoscope} label="Visite mediche" value={counts.visite} bg="#fef7d4" color="#854d0e" />
        <Kpi icon={HardHat} label="DPI" value={counts.dpi} bg="#fee2e2" color="#b91c1c" />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6">
        <Tabs defaultValue="scheda" className="w-full">
          <TabsList className="bg-slate-100 p-1 mb-4 flex-wrap h-auto">
            <TabTrigger value="scheda" icon={Building2}>Scheda azienda</TabTrigger>
            <TabTrigger value="lavoratori" icon={Users}>Lavoratori ({counts.workers})</TabTrigger>
            <TabTrigger value="formazione" icon={GraduationCap}>Formazione ({counts.formazione})</TabTrigger>
            <TabTrigger value="visite" icon={Stethoscope}>Visite mediche ({counts.visite})</TabTrigger>
            <TabTrigger value="dpi" icon={HardHat}>DPI ({counts.dpi})</TabTrigger>
            <TabTrigger value="allegati" icon={FolderArchive}>Allegati</TabTrigger>
          </TabsList>

          <TabsContent value="scheda" className="mt-2 space-y-4">
            <SchedaAzienda
              company={company}
              editable={user?.role !== 'client' && company?.status !== 'dismesso'}
              onEdit={() => setEditOpen(true)}
            />
            <ContrattoServiziPanel company={company} />
          </TabsContent>

          <TabsContent value="lavoratori" className="mt-2">
            <div className="flex items-center justify-end mb-3">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                <Checkbox checked={showDismessi} onCheckedChange={(v) => setShowDismessi(!!v)} data-testid="company-toggle-dismessi-workers" />
                <Archive size={13} className="text-slate-500" /> {showDismessi ? 'SOLO dismessi (attivi nascosti) — premi "Reinserisci" per riportarli in azienda' : 'Mostra SOLO dismessi'}
              </label>
            </div>
            {lw ? <Spinner /> : (
              <WorkersTable
                workers={workers}
                onEdit={user?.role !== 'client' ? (w) => workerCrud.onEdit(w) : null}
                onDelete={user?.role !== 'client' ? (w) => workerCrud.onDelete(w) : null}
                onReload={reloadWorkers}
                isClient={user?.role === 'client'}
                currentCompanyName={company?.name}
              />
            )}
          </TabsContent>

          <TabsContent value="formazione" className="mt-2">
            <FormazioneTable
              rows={formazione}
              workerName={workerName}
              onAdd={user?.role !== 'client' ? () => formazioneCrud.onCreate({ companyId: id }) : null}
              onEdit={user?.role !== 'client' ? (f) => formazioneCrud.onEdit(f) : null}
              onDelete={user?.role !== 'client' ? (f) => formazioneCrud.onDelete(f) : null}
            />
          </TabsContent>

          <TabsContent value="visite" className="mt-2">
            <VisiteAziendaleTable
              rows={sanitario?.items || []}
              visiteRaw={visiteRaw || []}
              companyId={id}
              isClient={user?.role === 'client'}
              onAdd={user?.role !== 'client' ? () => visitaCrud.onCreate({ companyId: id }) : null}
              onEdit={user?.role !== 'client' ? (v) => visitaCrud.onEdit(v) : null}
              onDelete={user?.role !== 'client' ? (v) => visitaCrud.onDelete(v) : null}
            />
          </TabsContent>

          <TabsContent value="dpi" className="mt-2">
            <DpiTable rows={dpi} workerName={workerName} />
          </TabsContent>

          <TabsContent value="allegati" className="mt-2">
            <AttachmentsPanel companyId={id} workers={workers || []} />
          </TabsContent>
        </Tabs>
      </div>

      <AutoProfileDialog
        open={autoOpen}
        onOpenChange={setAutoOpen}
        companyIds={[id]}
        onApplied={() => window.location.reload()}
      />

      <EntityFormModal
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Modifica scheda azienda"
        description="Aggiorna anagrafica e profilazione. Le celle vuote non sovrascrivono valori esistenti."
        fields={companyFields()}
        initialValues={company || {}}
        onSubmit={async (v) => {
          await companiesApi.update(id, v);
          setEditOpen(false);
          reloadCompany();
        }}
        submitLabel="Salva scheda"
      />

      <DismissCompanyDialog
        company={company}
        open={dismissOpen}
        onOpenChange={setDismissOpen}
        onDone={reloadCompany}
      />
      <ReactivateCompanyDialog
        company={company}
        open={reactivateOpen}
        onOpenChange={setReactivateOpen}
        onDone={reloadCompany}
      />

      <EntityFormModal
        open={workerCrud.open}
        onOpenChange={(v) => { if (!v) workerCrud.closeModal(); }}
        title={workerCrud.editing?.id ? 'Modifica lavoratore' : 'Nuovo lavoratore'}
        fields={workerFields([company])}
        initialValues={workerCrud.editing ? { ...workerCrud.editing, companyId: workerCrud.editing.companyId || id } : { companyId: id }}
        onSubmit={workerCrud.save}
      />

      <EntityFormModal
        open={formazioneCrud.open}
        onOpenChange={(v) => { if (!v) formazioneCrud.closeModal(); }}
        title={formazioneCrud.editing?.id ? 'Modifica corso' : 'Nuovo corso di formazione'}
        fields={formazioneFields(workers || [], [company], user?.role === 'client', id)}
        initialValues={formazioneCrud.editing ? { ...formazioneCrud.editing, companyId: formazioneCrud.editing.companyId || id } : { companyId: id }}
        onSubmit={formazioneCrud.save}
      />

      <EntityFormModal
        open={visitaCrud.open}
        onOpenChange={(v) => { if (!v) visitaCrud.closeModal(); }}
        title={visitaCrud.editing?.id ? 'Modifica visita medica' : 'Nuova visita medica'}
        fields={visitaFields(workers || [], [company], user?.role === 'client', id)}
        initialValues={visitaCrud.editing ? { ...visitaCrud.editing, companyId: visitaCrud.editing.companyId || id } : { companyId: id }}
        onSubmit={visitaCrud.save}
      />
    </div>
  );
};

function rischioHeaderClass(v) {
  const r = (v || '').toUpperCase();
  if (r === 'ALTO') return 'bg-red-500/40 border border-red-300 text-red-50';
  if (r === 'MEDIO') return 'bg-amber-500/40 border border-amber-300 text-amber-50';
  if (r === 'BASSO') return 'bg-emerald-500/40 border border-emerald-300 text-emerald-50';
  return 'bg-white/10 border border-white/20 text-white/90';
}

const Tag = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-semibold border border-white/15">
    <Icon size={13} className="text-[#FFCB05]" /> {children}
  </span>
);

// ── Scheda azienda: Anagrafica + Profilazione ────────────────────────
const SchedaAzienda = ({ company, editable, onEdit }) => {
  const anagrafica = [
    { label: 'Denominazione', value: company.name },
    { label: 'Partita IVA', value: company.piva, mono: true },
    { label: 'Codice Fiscale', value: company.codiceFiscale, mono: true },
    { label: 'Indirizzo', value: company.address, wide: true },
    { label: 'Codice ATECO', value: company.codiceAteco, mono: true },
    { label: 'Antincendio', value: company.antincendio },
    { label: 'Primo soccorso', value: company.primoSoccorso },
  ];
  const emails = Array.isArray(company.emails) && company.emails.length
    ? company.emails
    : (company.email ? [company.email] : []);

  const profilazione = [
    { label: 'N. sedi', value: company.sedi },
    { label: 'Macrosettore', value: company.macrosettore, wide: true },
    { label: 'Rischio', value: company.rischio, badge: rischioBadgeClass(company.rischio) },
    { label: 'RSPP CSC', value: company.rspp_csc, siNo: true },
    { label: 'Medico Competente', value: company.medicoCompetente },
    { label: 'Somministrazione HACCP', value: company.somministrazioneHACCP, siNo: true },
    { label: 'Supporto HACCP', value: company.supportoHACCP, siNo: true },
    { label: 'CPI', value: company.cpi, siNo: true },
    { label: 'Resp. Piscine', value: company.respPiscine },
    { label: 'Presenza Piscine', value: company.presenzaPiscine, siNo: true },
    { label: 'Legionella', value: company.legionella, siNo: true },
    { label: 'Cantieri', value: company.cantieri, siNo: true },
    { label: 'Contratto', value: company.contratto || '—' },
  ];

  return (
    <div className="space-y-6" data-testid="scheda-azienda">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-600">Scheda importata / modificabile manualmente</div>
        {editable && (
          <button
            onClick={onEdit}
            data-testid="edit-scheda-btn"
            className="ro-hide flex items-center gap-2 px-4 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-xs font-bold"
          >
            <Pencil size={14} /> MODIFICA SCHEDA
          </button>
        )}
      </div>

      <SectionCard title="Anagrafica" icon={IdCard} color="#023F80" bg="#e0e7ff">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {anagrafica.map((f) => (
            <FieldRow key={f.label} {...f} />
          ))}
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Email</div>
            {emails.length ? (
              <div className="flex flex-wrap gap-2">
                {emails.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-xs font-mono text-slate-700 border border-slate-200">
                    <Mail size={12} className="text-[#023F80]" /> {e}
                  </span>
                ))}
              </div>
            ) : <span className="text-slate-400 text-sm">—</span>}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Profilazione" icon={Wand2} color="#8a6608" bg="#fef7d4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
          {profilazione.map((f) => (
            <FieldRow key={f.label} {...f} />
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

const SectionCard = ({ title, icon: Icon, color, bg, children }) => (
  <div className="rounded-2xl border border-slate-200 overflow-hidden">
    <div className="px-4 py-3 flex items-center gap-2" style={{ background: bg }}>
      <Icon size={16} style={{ color }} />
      <h3 className="text-sm font-extrabold uppercase tracking-wider" style={{ color }}>{title}</h3>
    </div>
    <div className="p-4 sm:p-5 bg-white">{children}</div>
  </div>
);

const FieldRow = ({ label, value, mono, wide, siNo, badge }) => {
  const isEmpty = value == null || value === '' || value === '—';
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">{label}</div>
      {isEmpty ? (
        <span className="text-slate-300 text-sm italic">non valorizzato</span>
      ) : siNo ? (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${value === 'SI' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{value}</span>
      ) : badge ? (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${badge}`}>{value}</span>
      ) : (
        <div className={`text-sm text-slate-800 ${mono ? 'font-mono' : ''}`}>{value}</div>
      )}
    </div>
  );
};

function rischioBadgeClass(v) {
  const r = (v || '').toUpperCase();
  if (r === 'ALTO') return 'bg-red-100 text-red-800';
  if (r === 'MEDIO') return 'bg-amber-100 text-amber-800';
  if (r === 'BASSO') return 'bg-emerald-100 text-emerald-800';
  return 'bg-slate-100 text-slate-700';
}

const Kpi = ({ icon: Icon, label, value, bg, color }) => (
  <div className="rounded-2xl p-4" style={{ background: bg }}>
    <div className="flex items-center justify-between">
      <Icon size={18} style={{ color }} />
      <span className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</span>
    </div>
    <div className="mt-2 text-3xl font-extrabold" style={{ color }}>{value}</div>
  </div>
);

const TabTrigger = ({ value, icon: Icon, children }) => (
  <TabsTrigger
    value={value}
    className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white data-[state=inactive]:text-slate-700 rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-2"
  >
    <Icon size={14} /> {children}
  </TabsTrigger>
);

const EmptyState = ({ children }) => (
  <div className="py-10 text-center text-sm text-slate-400">{children}</div>
);

const WorkersTable = ({ workers, onEdit, onDelete, onReload, isClient, currentCompanyName }) => {
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [rehireTarget, setRehireTarget] = React.useState(null);
  const [dismissTarget, setDismissTarget] = React.useState(null);
  const [principal, setPrincipal] = React.useState(null);
  const [ruoliOpen, setRuoliOpen] = React.useState(false);
  const [ruoliWorker, setRuoliWorker] = React.useState(null);
  const [rolesCatalog, setRolesCatalog] = React.useState([]);
  const [reassignTarget, setReassignTarget] = React.useState(null);

  React.useEffect(() => {
    if (isClient) return; // solo consultant può leggere il catalogo
    let cancelled = false;
    rolesApi.catalog().then((r) => { if (!cancelled) setRolesCatalog(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isClient]);

  const labelOfRole = React.useCallback((key) => {
    const e = rolesCatalog.find((x) => x.roleKey === key);
    return e ? e.roleLabel : key;
  }, [rolesCatalog]);

  // Normalizza il campo ruoli in un array. Supporta:
  //  - array di roleKey (formato canonico)
  //  - stringa separata da virgole / punti-virgola / pipe / slash (legacy JOB81)
  const ruoliOf = React.useCallback((w) => {
    const r = w?.ruoli;
    if (!r) return [];
    if (Array.isArray(r)) return r.filter(Boolean);
    return String(r).split(/[;,|/]+/).map((s) => s.trim()).filter(Boolean);
  }, []);

  // Sort alfabetico sul NOME VISIBILE ("cognome nome" come mostrato in tabella), stile Excel.
  const [sortDir, setSortDir] = React.useState('asc');
  const sorted = React.useMemo(() => {
    if (!workers) return [];
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...workers].sort((a, b) => {
      const na = `${a.lastName || ''} ${a.firstName || ''}`.trim();
      const nb = `${b.lastName || ''} ${b.firstName || ''}`.trim();
      return na.localeCompare(nb, 'it', { sensitivity: 'base' }) * dir;
    });
  }, [workers, sortDir]);

  const openMerge = React.useCallback((w) => {
    setPrincipal(w);
    setMergeOpen(true);
  }, []);

  if (!workers || workers.length === 0) return <EmptyState>Nessun lavoratore registrato</EmptyState>;
  return (
    <div className="overflow-x-auto">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="text-[11px] text-slate-500" data-testid="company-workers-sort-hint">
          Ordinato per nome visibile ({sortDir === 'asc' ? 'A→Z' : 'Z→A'}).
        </p>
        <SortAZButton
          direction={sortDir}
          onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          testId="company-workers-sort-az"
        />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-gray-500">
            <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Lavoratore</th>
            <th className="text-left bg-gray-100 px-4 py-3">Codice fiscale</th>
            <th className="text-left bg-gray-100 px-4 py-3">Mansione</th>
            <th className="text-left bg-gray-100 px-4 py-3">Ruoli</th>
            <th className="text-left bg-gray-100 px-4 py-3">Sede</th>
            <th className="text-left bg-gray-100 px-4 py-3">Assunzione</th>
            <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
            <th className="bg-gray-100 rounded-r-xl px-4 py-3 text-right pr-4">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((w) => (
            <tr key={w.id} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`company-worker-row-${w.id}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#e0e7ff] text-[#023F80] flex items-center justify-center font-bold text-xs">
                    {(w.firstName || ' ').charAt(0)}{(w.lastName || ' ').charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{w.lastName} {w.firstName}</div>
                    <div className="text-xs text-gray-500">{fmtDate(w.birthDate)}</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600">{w.fiscalCode || '—'}</td>
              <td className="px-4 py-3 text-gray-700">{w.mansione || '—'}</td>
              <td className="px-4 py-3" data-testid={`company-worker-ruoli-cell-${w.id}`}>
                <div className="flex flex-wrap items-center gap-1">
                  {ruoliOf(w).length === 0 && (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                  {ruoliOf(w).slice(0, 4).map((rk, i) => (
                    <span
                      key={`${rk}-${i}`}
                      title={rk}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-100"
                    >
                      {labelOfRole(rk)}
                    </span>
                  ))}
                  {ruoliOf(w).length > 4 && (
                    <span className="text-[10px] text-slate-500">+{ruoliOf(w).length - 4}</span>
                  )}
                  {!isClient && (
                    <button
                      onClick={() => { setRuoliWorker(w); setRuoliOpen(true); }}
                      title="Modifica ruoli"
                      data-testid={`company-worker-ruoli-edit-${w.id}`}
                      className="ro-hide ml-1 p-1 rounded-md hover:bg-indigo-50 text-indigo-700"
                    >
                      <Pencil size={11} />
                    </button>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-gray-700">{w.sede || '—'}</td>
              <td className="px-4 py-3 text-gray-700">{fmtDate(w.dataAssunzione)}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold ${w.status !== 'dismesso' && w.stato === 'attivo' ? 'bg-[#dcfce7] text-[#15803d]' : 'bg-[#fee2e2] text-[#b91c1c]'}`}>
                  {w.status === 'dismesso' ? 'Dismesso' : w.stato === 'attivo' ? 'Attivo' : 'Cessato'}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1 justify-end">
                  {!isClient && (w.status === 'dismesso' || w.stato === 'cessato' || w.stato === 'dismesso') && (
                    <button
                      onClick={() => setRehireTarget(w)}
                      title="Reinserisci il lavoratore nell'azienda"
                      data-testid={`company-worker-rehire-${w.id}`}
                      className="ro-hide px-2 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center gap-1"
                    >
                      <UserPlus size={12} /> Reinserisci
                    </button>
                  )}
                  {!isClient && w.status !== 'dismesso' && w.stato !== 'cessato' && w.stato !== 'dismesso' && (
                    <button
                      onClick={() => setDismissTarget(w)}
                      title="Dismetti lavoratore (cessato)"
                      data-testid={`company-worker-dismiss-${w.id}`}
                      className="ro-hide p-1.5 rounded-md hover:bg-orange-50"
                    >
                      <UserMinus size={13} className="text-orange-600" />
                    </button>
                  )}
                  {onEdit && (
                    <button onClick={() => onEdit(w)} title="Modifica lavoratore" data-testid={`company-worker-edit-${w.id}`} className="ro-hide p-1.5 rounded-md hover:bg-gray-100">
                      <Pencil size={13} className="text-gray-600" />
                    </button>
                  )}
                  {!isClient && (
                    <button
                      onClick={() => setReassignTarget(w)}
                      title="Sposta lavoratore in un'altra azienda"
                      data-testid={`company-worker-reassign-${w.id}`}
                      className="ro-hide p-1.5 rounded-md hover:bg-indigo-50"
                    >
                      <ArrowRightLeft size={13} className="text-[#023F80]" />
                    </button>
                  )}
                  {!isClient && (
                    <button
                      onClick={() => openMerge(w)}
                      title="Unisci con un altro lavoratore (stessa persona)"
                      data-testid={`company-worker-merge-${w.id}`}
                      className="ro-hide p-1.5 rounded-md hover:bg-indigo-50"
                    >
                      <Merge size={13} className="text-[#023F80]" />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(w)}
                      title="Elimina lavoratore"
                      data-testid={`company-worker-delete-${w.id}`}
                      className="ro-hide p-1.5 rounded-md hover:bg-red-50"
                    >
                      <Trash2 size={13} className="text-red-600" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <MergeWorkerDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        principal={principal}
        workers={sorted}
        onDone={() => { setMergeOpen(false); if (onReload) onReload(); }}
      />

      <DismissWorkerDialog
        worker={dismissTarget}
        open={!!dismissTarget}
        onOpenChange={(v) => !v && setDismissTarget(null)}
        onDone={() => { setDismissTarget(null); if (onReload) onReload(); }}
      />

      <RehireWorkerDialog
        worker={rehireTarget}
        open={!!rehireTarget}
        onOpenChange={(v) => !v && setRehireTarget(null)}
        onDone={() => { setRehireTarget(null); if (onReload) onReload(); }}
      />

      <RuoliEditDialog
        open={ruoliOpen}
        onOpenChange={setRuoliOpen}
        worker={ruoliWorker}
        catalog={rolesCatalog}
        onDone={() => { setRuoliOpen(false); if (onReload) onReload(); }}
      />

      <ReassignCompanyDialog
        open={!!reassignTarget}
        onOpenChange={(v) => !v && setReassignTarget(null)}
        worker={reassignTarget}
        currentCompanyName={currentCompanyName}
        onDone={() => { setReassignTarget(null); if (onReload) onReload(); }}
      />
    </div>
  );
};

const RuoliEditDialog = ({ open, onOpenChange, worker, catalog, onDone }) => {
  const [selected, setSelected] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open && worker) setSelected(Array.isArray(worker.ruoli) ? [...worker.ruoli] : []);
    if (!open) { setSearch(''); }
  }, [open, worker]);

  if (!open || !worker) return null;

  const toggle = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };
  const filtered = (catalog || []).filter((e) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return e.roleLabel.toLowerCase().includes(s) || e.roleKey.toLowerCase().includes(s);
  });

  const save = async () => {
    setSaving(true);
    try {
      await rolesApi.updateWorkerRuoli(worker.id, selected, 'replace');
      toast.success('Ruoli aggiornati');
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio ruoli');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="ruoli-edit-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={() => onOpenChange(false)} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[min(96vw,560px)] max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-[#023F80]">Ruoli lavoratore</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {worker.lastName} {worker.firstName} · {worker.fiscalCode || 'CF assente'}
          </p>
        </div>
        <div className="px-5 py-3 border-b border-slate-100">
          <input
            type="text"
            placeholder="Filtra ruolo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="ruoli-edit-search"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
            <span>{selected.length} ruoli selezionati</span>
            {selected.length > 0 && (
              <button
                className="text-red-600 hover:underline"
                onClick={() => setSelected([])}
                data-testid="ruoli-edit-clear"
              >
                Deseleziona tutti
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {filtered.map((e) => {
              const isSel = selected.includes(e.roleKey);
              return (
                <button
                  key={e.roleKey}
                  onClick={() => toggle(e.roleKey)}
                  data-testid={`ruoli-edit-opt-${e.roleKey}`}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                    isSel
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-900 font-semibold'
                      : 'bg-white border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-3.5 h-3.5 rounded border ${isSel ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`} />
                    <span>{e.roleLabel}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 truncate">{e.roleKey}</div>
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">Nessun ruolo corrispondente</p>
          )}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
            data-testid="ruoli-edit-cancel"
          >
            Annulla
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60"
            data-testid="ruoli-edit-save"
          >
            {saving ? 'Salvo…' : 'Salva ruoli'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MergeWorkerDialog = ({ open, onOpenChange, principal, workers, onDone }) => {
  const [selectedId, setSelectedId] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [step, setStep] = React.useState('pick'); // pick | confirm
  const [crossCompany, setCrossCompany] = React.useState(false);
  const [allWorkers, setAllWorkers] = React.useState(null);
  const [companiesMap, setCompaniesMap] = React.useState({});
  const [loadingCross, setLoadingCross] = React.useState(false);

  React.useEffect(() => {
    if (!open) { setSelectedId(''); setSearch(''); setPreview(null); setStep('pick'); setCrossCompany(false); }
  }, [open]);

  // Carica lista completa lavoratori + mappa aziende quando l'utente attiva cross-company
  React.useEffect(() => {
    if (!crossCompany || allWorkers) return;
    let cancelled = false;
    setLoadingCross(true);
    Promise.all([
      workersApi.list({ status: 'all' }).then((r) => r.data).catch(() => []),
      companiesApi.list({ limit: 5000 }).then((r) => r.data).catch(() => []),
    ]).then(([ws, cs]) => {
      if (cancelled) return;
      const map = {};
      (cs || []).forEach((c) => { map[c.id] = c.name; });
      setCompaniesMap(map);
      setAllWorkers(ws || []);
    }).finally(() => { if (!cancelled) setLoadingCross(false); });
    return () => { cancelled = true; };
  }, [crossCompany, allWorkers]);

  if (!open || !principal) return null;

  const sourceList = crossCompany ? (allWorkers || []) : (workers || []);
  const candidates = sourceList.filter((w) => w.id !== principal.id).filter((w) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (w.firstName || '').toLowerCase().includes(s)
      || (w.lastName || '').toLowerCase().includes(s)
      || (w.fiscalCode || '').toLowerCase().includes(s);
  }).slice(0, 200); // limite di sicurezza UI

  const companyNameOf = (w) => {
    if (crossCompany) return companiesMap[w.companyId] || w.companyId || '—';
    return null; // stessa azienda del principale, non serve mostrarla
  };

  const goPreview = async () => {
    if (!selectedId) { toast.error('Seleziona il lavoratore da unire'); return; }
    setLoading(true);
    try {
      const { data } = await workersApi.mergePreview(principal.id, [selectedId]);
      setPreview(data);
      setStep('confirm');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore anteprima');
    } finally { setLoading(false); }
  };

  const doApply = async () => {
    setLoading(true);
    try {
      const { data } = await workersApi.mergeApply(principal.id, [selectedId]);
      toast.success(`Unione eseguita (opId: ${data.opId}). Puoi annullarla dalla sezione Riconciliazione.`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore unione');
    } finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="merge-worker-dialog"
      onClick={() => onOpenChange(false)}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 bg-gradient-to-r from-[#023F80] to-[#022F60] text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Merge size={18} className="text-[#FFCB05]" />
            <h3 className="font-bold">Unisci lavoratore</h3>
          </div>
          <button onClick={() => onOpenChange(false)} className="text-white/70 hover:text-white" data-testid="merge-close-btn">✕</button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Lavoratore principale (rimane)</div>
            <div className="text-sm font-bold text-emerald-900 mt-1">
              {principal.lastName} {principal.firstName}
            </div>
            <div className="text-[11px] text-emerald-800 font-mono">{principal.fiscalCode || '(nessun CF)'} · id={principal.id}</div>
          </div>

          {step === 'pick' && (
            <>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Cerca il duplicato da unire (rimarrà archiviato)</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome, cognome o codice fiscale…"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-2"
                data-testid="merge-search-input"
              />
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-3 cursor-pointer select-none" data-testid="merge-cross-company-toggle">
                <input
                  type="checkbox"
                  checked={crossCompany}
                  onChange={(e) => setCrossCompany(e.target.checked)}
                />
                Cerca in tutte le aziende {loadingCross && <Loader2 size={12} className="animate-spin text-[#023F80]" />}
                {crossCompany && !loadingCross && allWorkers && (
                  <span className="text-[11px] text-slate-500 font-normal">({allWorkers.length} lavoratori totali)</span>
                )}
              </label>
              {crossCompany && !search && (
                <div className="mb-2 p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                  Ricerca cross-azienda attiva: digita almeno un carattere per filtrare la lista. L'unione NON cambierà l'azienda del lavoratore principale.
                </div>
              )}
              <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {candidates.length === 0 && (
                  <div className="p-4 text-center text-xs text-slate-400">
                    {crossCompany ? 'Nessun candidato che corrisponde alla ricerca.' : 'Nessun candidato in questa azienda.'}
                  </div>
                )}
                {candidates.map((w) => {
                  const cName = companyNameOf(w);
                  const sameCompany = crossCompany && w.companyId === principal.companyId;
                  return (
                    <label
                      key={w.id}
                      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 ${selectedId === w.id ? 'bg-indigo-50' : ''}`}
                      data-testid={`merge-candidate-${w.id}`}
                    >
                      <input
                        type="radio"
                        name="merge-target"
                        checked={selectedId === w.id}
                        onChange={() => setSelectedId(w.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800">
                          {w.lastName} {w.firstName}
                          {w._archived && <span className="ml-2 text-[10px] uppercase tracking-widest bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">archiviato</span>}
                          {w.stato === 'dismesso' && <span className="ml-2 text-[10px] uppercase tracking-widest bg-red-100 text-red-700 px-1.5 py-0.5 rounded">cessato</span>}
                        </div>
                        <div className="text-[11px] text-slate-600 mt-0.5 space-x-3">
                          <span className="font-mono">CF: {w.fiscalCode || '—'}</span>
                          <span>DN: {fmtDate(w.birthDate) || '—'}</span>
                          <span className="font-mono text-slate-400">id={w.id}</span>
                        </div>
                        {cName && (
                          <div className={`text-[11px] mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded ${sameCompany ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                            <Building2 size={10} /> {cName}
                            {sameCompany && <span className="text-[9px] uppercase font-bold">(stessa azienda)</span>}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700">Annulla</button>
                <button
                  onClick={goPreview}
                  disabled={loading || !selectedId}
                  data-testid="merge-preview-btn"
                  className="px-4 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] disabled:opacity-50 text-white text-sm font-bold flex items-center gap-2"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Merge size={14} />}
                  Anteprima unione
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && preview && (
            <>
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Duplicato da unire (verrà archiviato)</div>
                {preview.duplicates?.map((d) => (
                  <div key={d.id} className="mt-1">
                    <div className="text-sm font-bold text-amber-900">{d.name}</div>
                    <div className="text-[11px] text-amber-800 font-mono">id={d.id}</div>
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Record collegati che verranno spostati sul principale</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(preview.linked_to_move || {}).map(([k, v]) => (
                    <div key={k} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center" data-testid={`merge-linked-${k}`}>
                      <div className="text-2xl font-extrabold text-[#023F80]">{v}</div>
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mt-1">{k}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-xs text-slate-500 mb-4 leading-relaxed">
                L'operazione è <strong>reversibile</strong>: crea un backup completo delle collezioni prima di scrivere.
                Se serve annullare, apri la sezione Riconciliazione → Operazioni → Rollback.
              </div>

              <div className="flex justify-between gap-2">
                <button onClick={() => setStep('pick')} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700" data-testid="merge-back-btn">← Cambia selezione</button>
                <div className="flex gap-2">
                  <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700">Annulla</button>
                  <button
                    onClick={doApply}
                    disabled={loading}
                    data-testid="merge-confirm-btn"
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-bold flex items-center gap-2"
                  >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Merge size={14} />}
                    Conferma unione
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const VisiteAziendaleTable = ({ rows, visiteRaw, companyId, isClient, onAdd, onEdit, onDelete }) => {
  const [attachVisita, setAttachVisita] = React.useState(null);
  const [attachWorkerName, setAttachWorkerName] = React.useState('');
  // Valori canonici di tipoVisita: se il campo contiene altro (es.
  // "SPIROMETRIA ANNUALE" da vecchi import) lo trattiamo come accertamento
  // per non "sporcare" la colonna Tipologia.
  const CANONICAL_TIPO = new Set([
    'Preventiva', 'Periodica', 'Cambio mansione', 'Rientro malattia',
    'Ripresa dopo infortunio', 'Su richiesta del lavoratore', 'Visita preassuntiva',
  ]);
  const isCanonicalTipo = (t) => t && CANONICAL_TIPO.has(String(t).trim());
  const badge = (s) => {
    const map = {
      valida:      { bg: 'bg-emerald-100', color: 'text-emerald-800', label: 'Valida' },
      in_scadenza: { bg: 'bg-amber-100',   color: 'text-amber-800',   label: 'In scadenza' },
      scaduta:     { bg: 'bg-red-100',     color: 'text-red-800',     label: 'Scaduta' },
      da_fare:     { bg: 'bg-violet-100',  color: 'text-violet-800',  label: 'Da fare' },
    };
    const m = map[s] || { bg: 'bg-slate-100', color: 'text-slate-700', label: s };
    return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${m.bg} ${m.color}`}>{m.label}</span>;
  };
  // Trova l'ultima visita raw per il lavoratore, per abilitare modifica/eliminazione
  const latestVisitOf = React.useCallback((workerId) => {
    const visits = (visiteRaw || []).filter((v) => v.workerId === workerId);
    if (visits.length === 0) return null;
    return [...visits].sort((a, b) => (
      String(b.scadenza || b.dataVisita || '').localeCompare(String(a.scadenza || a.dataVisita || ''))
    ))[0];
  }, [visiteRaw]);

  // Ordinamento alfabetico sul NOME VISIBILE (come letto in tabella), stile Excel.
  const [sortDir, setSortDir] = React.useState('asc');
  const sortedRows = React.useMemo(() => {
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...(rows || [])].sort((a, b) => {
      const na = String(a.workerName || `${a.firstName || ''} ${a.lastName || ''}`).trim();
      const nb = String(b.workerName || `${b.firstName || ''} ${b.lastName || ''}`).trim();
      return na.localeCompare(nb, 'it', { sensitivity: 'base' }) * dir;
    });
  }, [rows, sortDir]);

  return (
    <div className="overflow-x-auto" data-testid={`visite-aziendale-${companyId}`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <p className="text-xs text-slate-500">Allineato a &quot;Sorveglianza sanitaria&quot;: una posizione per lavoratore attivo, basata sull&apos;ultima visita.</p>
        <div className="flex items-center gap-2">
          <SortAZButton
            direction={sortDir}
            onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            testId={`visite-aziendale-sort-az-${companyId}`}
          />
          {onAdd && (
          <button
            onClick={onAdd}
            data-testid={`visite-aziendale-add-${companyId}`}
            className="ro-hide inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#fef7d4] text-[#8a6608] hover:bg-[#E6B800]"
          >
            <Plus size={13} /> Nuova visita
          </button>
        )}
        </div>
      </div>
      {(!rows || rows.length === 0) ? (
        <EmptyState>
          Nessuna posizione sanitaria attiva
          {onAdd && (
            <div className="mt-3">
              <button onClick={onAdd} className="text-xs font-semibold text-[#023F80] hover:underline">
                + Aggiungi la prima visita
              </button>
            </div>
          )}
        </EmptyState>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Lavoratore</th>
              <th className="text-left bg-gray-100 px-4 py-3">Tipologia</th>
              <th className="text-left bg-gray-100 px-4 py-3">Ultima visita</th>
              <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
              <th className="text-left bg-gray-100 px-4 py-3">Idoneità</th>
              <th className="text-left bg-gray-100 px-4 py-3">Accertamenti</th>
              <th className="text-left bg-gray-100 px-4 py-3">Medico</th>
              <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
              {!isClient && <th className="text-right bg-gray-100 rounded-r-xl px-4 py-3">Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const visita = latestVisitOf(r.workerId);
              const rawTipo = visita?.tipoVisita || r.tipoVisita || visita?.tipo || r.tipo || '';
              const tipoIsCanonical = isCanonicalTipo(rawTipo);
              const tipoVisita = tipoIsCanonical ? rawTipo : '';
              // Se il tipo non è canonico, lo aggiungiamo agli accertamenti (retro-compat).
              const rawAccert = visita?.accertamenti || r.accertamenti || '';
              const accertamenti = tipoIsCanonical ? rawAccert : (rawAccert ? `${rawTipo}; ${rawAccert}` : rawTipo);
              return (
                <tr key={r.workerId} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`visite-aziendale-row-${r.workerId}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{r.workerName || '—'}</div>
                    <div className="text-[11px] text-slate-500">{r.mansione || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs">
                    {tipoVisita ? (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[10px] font-semibold">{tipoVisita}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(r.ultimaVisita) || <span className="text-slate-300 italic">nessuna</span>}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(r.scadenza) || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs">{r.idoneita || '—'}</td>
                  <td className="px-4 py-3 text-gray-700 text-[11px] max-w-xs">
                    {accertamenti ? (
                      <span className="line-clamp-2" title={accertamenti}>{accertamenti}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs">{r.medico || '—'}</td>
                  <td className="px-4 py-3">{badge(r.stato)}</td>
                  {!isClient && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {visita && onEdit && (
                          <button
                            onClick={() => onEdit(visita)}
                            title="Modifica ultima visita"
                            data-testid={`visite-aziendale-edit-${r.workerId}`}
                            className="ro-hide p-1.5 rounded-md hover:bg-gray-100"
                          >
                            <Pencil size={13} className="text-gray-600" />
                          </button>
                        )}
                        {visita && (
                          <button
                            onClick={() => { setAttachVisita(visita); setAttachWorkerName(r.workerName || ''); }}
                            title="Allegati della visita"
                            data-testid={`visite-aziendale-attach-${r.workerId}`}
                            className="ro-hide p-1.5 rounded-md hover:bg-[#fef7d4]"
                          >
                            <Paperclip size={13} className="text-[#8a6608]" />
                          </button>
                        )}
                        {!visita && onAdd && (
                          <button
                            onClick={() => onAdd()}
                            title="Aggiungi visita"
                            data-testid={`visite-aziendale-addrow-${r.workerId}`}
                            className="ro-hide p-1.5 rounded-md hover:bg-emerald-50"
                          >
                            <Plus size={13} className="text-emerald-700" />
                          </button>
                        )}
                        {visita && onDelete && (
                          <button
                            onClick={() => onDelete(visita)}
                            title="Elimina ultima visita"
                            data-testid={`visite-aziendale-del-${r.workerId}`}
                            className="ro-hide p-1.5 rounded-md hover:bg-red-50"
                          >
                            <Trash2 size={13} className="text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <VisitaAttachmentsDialog
        open={!!attachVisita}
        onOpenChange={(v) => { if (!v) { setAttachVisita(null); setAttachWorkerName(''); } }}
        visita={attachVisita}
        workerName={attachWorkerName}
      />
    </div>
  );
};

const FormazioneTable = ({ rows, workerName, onAdd, onEdit, onDelete }) => {
  // Backend already returns recomputed stato + categoria on every row.
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [groupByCorso, setGroupByCorso] = React.useState(true);
  const [sortDir, setSortDir] = React.useState('asc');

  const filtered = React.useMemo(() => {
    let list = rows || [];
    if (statusFilter !== 'all') list = list.filter((f) => f.stato === statusFilter);
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      const ca = categoryLabel(a.categoria || 'altro').toLowerCase();
      const cb = categoryLabel(b.categoria || 'altro').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      const wa = workerName(a.workerId).toLowerCase();
      const wb = workerName(b.workerId).toLowerCase();
      return wa.localeCompare(wb) * dir;
    });
  }, [rows, statusFilter, workerName, sortDir]);

  // Summary by canonical category
  const typesSummary = React.useMemo(() => {
    const map = new Map();
    (rows || []).forEach((f) => {
      const k = categoryLabel(f.categoria || 'altro');
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  if (!rows || rows.length === 0) {
    return (
      <div>
        {onAdd && (
          <div className="flex justify-end mb-3">
            <button
              onClick={onAdd}
              data-testid="company-add-formazione-btn"
              className="ro-hide inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-xs font-bold"
            >
              <Plus size={14} /> Nuovo corso
            </button>
          </div>
        )}
        <EmptyState>Nessun corso di formazione</EmptyState>
      </div>
    );
  }

  const counts = {
    all: rows.length,
    valido: rows.filter((f) => f.stato === 'valido').length,
    in_scadenza: rows.filter((f) => f.stato === 'in_scadenza').length,
    scaduto: rows.filter((f) => f.stato === 'scaduto').length,
  };

  return (
    <div className="space-y-3">
      {/* Tipi di corso */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="formazione-types-summary">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 mb-2">Tipologie di corso ({typesSummary.length})</div>
        <div className="flex flex-wrap gap-1.5">
          {typesSummary.map(([name, count]) => (
            <span key={name} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${name.startsWith('[') ? 'bg-amber-50 text-amber-700 border-amber-200 italic' : 'bg-white text-[#023F80] border-[#e0e7ff]'}`}>
              {name}
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#023F80]/10 text-[10px]">{count}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Filtri stato + opzioni */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {[
            { key: 'all', label: 'Tutti', n: counts.all },
            { key: 'valido', label: 'Validi', n: counts.valido },
            { key: 'in_scadenza', label: 'In scadenza', n: counts.in_scadenza },
            { key: 'scaduto', label: 'Scaduti', n: counts.scaduto },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              data-testid={`formazione-filter-${t.key}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${statusFilter === t.key ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {t.label} ({t.n})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <SortAZButton
            direction={sortDir}
            onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            testId="company-formazione-sort-az"
          />
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={groupByCorso} onChange={(e) => setGroupByCorso(e.target.checked)} />
            Raggruppa per corso
          </label>
          {onAdd && (
            <button
              onClick={onAdd}
              data-testid="company-add-formazione-btn"
              className="ro-hide inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] text-white text-xs font-bold"
            >
              <Plus size={14} /> Nuovo corso
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Corso</th>
              <th className="text-left bg-gray-100 px-4 py-3">Lavoratore</th>
              <th className="text-left bg-gray-100 px-4 py-3">Rilascio</th>
              <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
              <th className="text-left bg-gray-100 px-4 py-3">Durata</th>
              <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
              <th className="bg-gray-100 rounded-r-xl px-4 py-3 text-right pr-4">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f, i) => {
              const prev = i > 0 ? filtered[i - 1] : null;
              const sameAsPrev = groupByCorso && prev && (prev.corso || '') === (f.corso || '');
              return (
                <tr key={f.id} className={`border-b border-gray-50 hover:bg-gray-50/60 ${groupByCorso && !sameAsPrev && i > 0 ? 'border-t-2 border-t-slate-200' : ''}`}>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    {sameAsPrev ? <span className="text-gray-300">↳</span> : (f.corso || <span className="text-amber-700 italic text-xs">[Titolo da recuperare]</span>)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{workerName(f.workerId)}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(f.dataRilascio)}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(f.scadenza)}</td>
                  <td className="px-4 py-3 text-gray-700">{f.durata || 0}h</td>
                  <td className="px-4 py-3">{statusBadge(f.stato)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {onEdit && (
                        <button onClick={() => onEdit(f)} title="Modifica corso" data-testid={`company-formazione-edit-${f.id}`} className="ro-hide p-1.5 rounded-md hover:bg-gray-100">
                          <Pencil size={13} className="text-gray-600" />
                        </button>
                      )}
                      {onDelete && (
                        <button onClick={() => onDelete(f)} title="Elimina corso" data-testid={`company-formazione-delete-${f.id}`} className="ro-hide p-1.5 rounded-md hover:bg-red-50">
                          <Trash2 size={13} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-8 text-center text-sm text-slate-400">Nessun corso per il filtro selezionato</div>}
      </div>
    </div>
  );
};

const VisiteTable = ({ rows, workerName }) => {
  if (!rows || rows.length === 0) return <EmptyState>Nessuna visita medica</EmptyState>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-gray-500">
            <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Tipo</th>
            <th className="text-left bg-gray-100 px-4 py-3">Lavoratore</th>
            <th className="text-left bg-gray-100 px-4 py-3">Data visita</th>
            <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
            <th className="text-left bg-gray-100 px-4 py-3">Medico</th>
            <th className="text-left bg-gray-100 px-4 py-3">Idoneità</th>
            <th className="text-left bg-gray-100 rounded-r-xl px-4 py-3">Stato</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v) => (
            <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50/60">
              <td className="px-4 py-3 font-semibold text-gray-800">{v.tipo || '—'}</td>
              <td className="px-4 py-3 text-gray-700">{workerName(v.workerId)}</td>
              <td className="px-4 py-3 text-gray-700">{fmtDate(v.dataVisita)}</td>
              <td className="px-4 py-3 text-gray-700">{fmtDate(v.scadenza)}</td>
              <td className="px-4 py-3 text-gray-700">{v.medico || '—'}</td>
              <td className="px-4 py-3 text-gray-700 text-xs">{v.idoneita || '—'}</td>
              <td className="px-4 py-3">{statusBadge(v.stato)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DpiTable = ({ rows, workerName }) => {
  const [sortDir, setSortDir] = React.useState('asc');
  const sorted = React.useMemo(() => {
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...(rows || [])].sort((a, b) => {
      const wa = String(workerName(a.workerId) || '').toLowerCase();
      const wb = String(workerName(b.workerId) || '').toLowerCase();
      return wa.localeCompare(wb, 'it') * dir;
    });
  }, [rows, workerName, sortDir]);
  if (!rows || rows.length === 0) return <EmptyState>Nessun DPI consegnato</EmptyState>;
  return (
    <div className="overflow-x-auto">
      <div className="flex items-center justify-end mb-2">
        <SortAZButton
          direction={sortDir}
          onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          testId="company-dpi-sort-az"
        />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-gray-500">
            <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Tipo</th>
            <th className="text-left bg-gray-100 px-4 py-3">Lavoratore</th>
            <th className="text-left bg-gray-100 px-4 py-3">Consegna</th>
            <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
            <th className="text-left bg-gray-100 px-4 py-3">Taglia</th>
            <th className="text-left bg-gray-100 rounded-r-xl px-4 py-3">Stato</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => (
            <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50/60">
              <td className="px-4 py-3 font-semibold text-gray-800">{d.tipo || '—'}</td>
              <td className="px-4 py-3 text-gray-700">{workerName(d.workerId)}</td>
              <td className="px-4 py-3 text-gray-700">{fmtDate(d.dataConsegna)}</td>
              <td className="px-4 py-3 text-gray-700">{fmtDate(d.scadenza)}</td>
              <td className="px-4 py-3 text-gray-700">{d.taglia || '—'}</td>
              <td className="px-4 py-3">{statusBadge(d.stato === 'consegnato' ? 'valido' : d.stato)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyDetailPage;
